import AuthService from './AuthService';
import { API_URL } from '../global/global';
import axios from 'axios';

interface AIResponse {
  success: boolean;
  message?: string;
  user_message?: string;
  ai_response?: string;
  audio_base64?: string;
  speech_marks?: any[]; // Speech marks detallados
  viseme_marks?: any[]; // Marcas de visemas para lip sync
  phoneme_count?: number; // Número de fonemas detectados
  model_id?: string;
  voice_id?: string;
  audio_duration?: number; // Duración del audio en ms
  processing_times?: { // Tiempos detallados
    total: number;
    transcribe: number;
    claude: number;
    polly: number;
  };
  endpoint_used?: string; // Endpoint consumido
  download_link?: string; // Link para descargar Excel
  data_summary?: {
    total_records: number;
    processing_time: number;
    confidence: number;
    has_excel_data?: boolean;
  };
  error?: string;
}

class AIService {
  /**
   * Send text message to Claude Sonnet 4 (regular)
   */
  static async sendTextMessage(message: string, includeAudio: boolean = false): Promise<AIResponse> {
    try {
      const axios = AuthService.getAuthenticatedAxios();
      
      const response = await axios.post('/ai/chat', {
        message: message,
        include_audio: includeAudio
      });

      return response.data;
    } catch (error: any) {
      console.error('Error sending message:', error);
      
      return {
        success: false,
        error: error.response?.data?.message || 'Error de conexión con el servidor',
        message: 'No se pudo enviar el mensaje'
      };
    }
  }

  /**
   * Send smart text query (with endpoint consumption)
   */
  static async sendSmartTextQuery(message: string): Promise<AIResponse> {
    try {
      const axios = AuthService.getAuthenticatedAxios();

      console.log('🧠 Sending smart text query:', message);

      const response = await axios.post('/smart/smart-text-query', {
        message: message
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 45000 // 45 second timeout for endpoint calls
      });

      return response.data;
    } catch (error: any) {
      console.error('Error sending smart text query:', error);
      
      return {
        success: false,
        error: error.response?.data?.message || 'Error de conexión con el servidor',
        message: 'No se pudo procesar la consulta inteligente'
      };
    }
  }

  /**
   * Send smart voice query (with endpoint consumption)
   */
  static async sendSmartVoiceQuery(text: string): Promise<AIResponse> {
    try {
      const axios = AuthService.getAuthenticatedAxios();

      console.log('🎤 Sending smart voice query:', text);

      const response = await axios.post('/smart/smart-voice-query', {
        text: text
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 45000 // 45 second timeout for endpoint calls
      });

      return response.data;
    } catch (error: any) {
      console.error('Error sending smart voice query:', error);
      
      return {
        success: false,
        error: error.response?.data?.message || 'Error de conexión con el servidor',
        message: 'No se pudo procesar la consulta de voz inteligente'
      };
    }
  }

  /**
   * Send query to Claude 4 Sonnet MCP Decision System (MAIN METHOD)
   */
  static async sendMCPVoiceQuery(
    text: string, 
    skipAudio = false,
    onChunk?: (chunk: string) => void
  ): Promise<AIResponse> {
    try {
      const user = AuthService.getCurrentUser();
      const token = localStorage.getItem('skybot_token');

      const response = await fetch(`${API_URL}/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: text,
          user_id: 1,
          name_user: user?.name || user?.username || "Usuario",
          context: { skip_audio: skipAudio }
        })
      });

      if (!response.ok) throw new Error('Error en la respuesta');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      let downloadLink = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (!line.trim()) continue;
            
            const jsonStr = line.replace(/^data:\s*/, '');
            try {
              const parsed = JSON.parse(jsonStr);
              if (parsed.type === 'chunk' && parsed.content) {
                fullText += parsed.content;
                onChunk?.(parsed.content);
              } else if (parsed.type === 'download_link' && parsed.link) {
                downloadLink = parsed.link;
              }
            } catch (e) {}
          }
        }
      }

      return {
        success: true,
        message: "Consulta procesada",
        user_message: text,
        ai_response: fullText,
        download_link: downloadLink || undefined,
        endpoint_used: "stream"
      };
    } catch (error: any) {
      console.error('Error with Claude 4 Sonnet MCP:', error);
      
      return {
        success: false,
        error: error.message || 'Error de conexión con Claude 4 Sonnet',
        message: 'No se pudo procesar la consulta con Claude 4 Sonnet MCP'
      };
    }
  }

  /**
   * Test ultra-fast voice-text processing
   */
  static async testVoiceText(text: string): Promise<AIResponse> {
    try {
      const axios = AuthService.getAuthenticatedAxios();

      console.log('🧪 Testing ultra-fast voice-text processing:', text);

      const response = await axios.post('/ai/test-voice-text', {
        text: text
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000 // 10 second timeout
      });

      return response.data;
    } catch (error: any) {
      console.error('Error testing voice text:', error);
      
      return {
        success: false,
        error: error.response?.data?.message || 'Error de conexión con el servidor',
        message: 'No se pudo probar el procesamiento de texto'
      };
    }
  }

  /**
   * Download Excel report
   */
  static async downloadExcelReport(downloadLink: string): Promise<void> {
    try {
      console.log('📥 Downloading Excel report:', downloadLink);

      // Create a direct axios instance without baseURL to avoid duplication
      const directAxios = axios.create({
        timeout: 30000,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skybot_token')}`
        }
      });

      // Remove /api prefix from downloadLink to avoid duplication
      const cleanLink = downloadLink.startsWith('/api/') ? downloadLink.substring(4) : downloadLink;
      const fullUrl = `${API_URL}${cleanLink}`;
      console.log('📥 Original download link:', downloadLink);
      console.log('📥 Clean link:', cleanLink);
      console.log('📥 Full download URL:', fullUrl);

      const response = await directAxios.get(fullUrl, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Reporte.xlsx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ Excel report downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading Excel report:', error);
      throw new Error('No se pudo descargar el reporte Excel');
    }
  }

  /**
   * Send pre-transcribed text for ultra-fast processing (fallback)
   */
  static async sendVoiceText(text: string): Promise<AIResponse> {
    try {
      const axios = AuthService.getAuthenticatedAxios();

      console.log('⚡ Sending pre-transcribed text:', text);

      const response = await axios.post('/ai/voice-text-fast', {
        text: text
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 30000 // 30 second timeout
      });

      return response.data;
    } catch (error: any) {
      console.error('Error sending voice text:', error);
      
      return {
        success: false,
        error: error.response?.data?.message || 'Error de conexión con el servidor',
        message: 'No se pudo procesar el texto'
      };
    }
  }
}

export default AIService;
